import {Model} from 'MWC';
import RealInterestRate from '../../Credit/helper/realInterestRate';

const oneTimeCommission = 'one_time_commission',
    monthCommission = 'month_commission',
    insuranceRate = 'insurance_rate',
    timeUnit = 'time_unit',
    months = 12,
    hundredPercent = 100,
    daysInTheYear = 365,
    bankDays = 360;

export default class extends Model {
    static fields;

    constructor(data, type) {
        super();
        this.insured = false;
        this.selectedType = type;
        this.creditSum = data.min;
        this.requiredPayment = 0;
        this.overpayment = 0;
        this.creditCost = 0;
        this.loanTerm = 0;
        this.realInterestRate = 0;
        this.creditTerm = data.terms[0];
        this._initFields(data);
    }

    _initFields(data) {
        this.fields = {
            ...data
        };
    }

    changeField(name, value) {
        this[name] = value;

        this.calculateIndicators();
    }

    getCreditSum() {
        return +this.creditSum;
    }

    getOneTimeCommission() {
        return +this.fields[oneTimeCommission];
    }

    getMonthCommission() {
        return +this.fields[monthCommission];
    }

    checkInsurance() {
        return this.insured;
    }

    switchInsurance() {
        this.insured = !this.insured;

        this.calculateIndicators();
    }

    getInsuranceRate() {
        return this.checkInsurance() ? this.fields[insuranceRate] : 0;
    }

    getCurrentRate() {
        return this.round(+this.fields.rate * this.getCreditTerm());
    }

    getTimeUnit() {
        return this.fields[timeUnit];
    }

    getCurrentLoanTerm() {
        return this.loanTerm;
    }

    getCreditTerm() {
        return +this.creditTerm;
    }

    getMinSumEdge() {
        return this.fields.min;
    }

    getMaxSumEdge() {
        return this.fields.max;
    }

    getSumStep() {
        return this.fields.step;
    }

    getTerms() {
        return this.fields.terms;
    }

    getMinTermEdge() {
        return this.fields.terms[0];
    }

    getMaxTermEdge() {
        return this.fields.terms[this.fields.terms.length - 1];
    }

    getTermStep() {
        return 1;
    }

    getRequiredPayment() {
        return this.requiredPayment;
    }

    setRequiredPayment(value) {
        this.requiredPayment = value;
    }

    getOverpayment() {
        return this.overpayment;
    }

    setOverpayment(value) {
        this.overpayment = value;
    }

    getCreditCost() {
        return this.creditCost;
    }

    setCreditCost(value) {
        this.creditCost = value;
    }

    calculateIndicators() {
        const requestedSum = this.getCreditSum(),
            creditTerm = this.getCreditTerm(),
            rate = this.getCurrentRate(),
            insuranceRate = this.getInsuranceRate(),
            commission = this.getOneTimeCommission(),
            monthCommission = this.getMonthCommission();

        this.fullCreditSum = this.round(
            requestedSum +
                (requestedSum * commission) / hundredPercent +
                ((((requestedSum * commission) / hundredPercent + requestedSum) * insuranceRate) /
                    hundredPercent) *
                    creditTerm
        );

        const requiredPayment = this.round(
            (((this.fullCreditSum * rate) / months / hundredPercent) * daysInTheYear) /
                bankDays /
                (1 -
                    Math.pow(
                        1 + ((rate / months / hundredPercent) * daysInTheYear) / bankDays,
                        -creditTerm
                    )) +
                (this.fullCreditSum * monthCommission) / hundredPercent
        );

        this.setRequiredPayment(requiredPayment);

        const overpayment = this.round(requiredPayment * creditTerm - requestedSum);
        this.setOverpayment(overpayment);

        const creditCost = requestedSum + overpayment;
        this.setCreditCost(creditCost);
        this.calculateRealInterestRate();
    }

    round(value) {
        return Math.round(value * 100) / 100;
    }

    calculateRealInterestRate() {
        const realInterestRate = new RealInterestRate(
            this.requiredPayment,
            this.creditTerm,
            this.fullCreditSum
        );
        this.realInterestRate = realInterestRate.calculate();
    }

    getRealInterestRate() {
        return (this.realInterestRate * 100).toFixed(2);
    }
}
